import { getRouter } from '@platform/boot';
import { ComponentWrapper } from '@platform/shared/helpers/components';
import { v4 as guid } from 'uuid';
import type { Component, ComputedRef, VNode } from 'vue';
import { reactive } from 'vue';
import type { Router } from 'vue-router';
import config from '@platform/shared/config';
import { cloneDeep } from 'lodash-es';

type Tab = {
    key: string;
    fullPath: string;
    path: string;
    label: string;
    hasUnsavedData?: Set<string> | null;
    cname?: string;
} & (
    | {
          component: Component;
          isDynamicRoute: true;
          renderSlots: Record<string, () => VNode>;
          props?: unknown | ComputedRef<unknown>;
          onClose?: () => boolean;
      }
    | { isDynamicRoute?: false | undefined }
);
export type TabsStoreType = {
    tabs: Tab[];
    selectedTab: string | null;
    excludedTabs: string[];
};
function getLocalTabs() {
    const setting = config.getItemStorage(config.systemPreference)?.other ?? { rememberTabs: true };
    if (setting?.rememberTabs) {
        const userId = JSON.parse(localStorage.getItem('SYS_PARAMS') || '{}')?.currUserUuid;
        const list: Tab[] = JSON.parse(localStorage.getItem(`tabs:${userId}`) || '[]');
        return list
            .filter((tab) => {
                if (tab.isDynamicRoute) return false;
                // TODO: потом добавить - нужно только для миграции
                // if (tab.key && !tab.key.startsWith('/') && tab.key.slice(0, 1) === tab.key.slice(0, 1).toUpperCase())
                //     return true;
                return true;
            })
            .map((tab) => ({ ...tab, hasUnsavedData: undefined }));
    } else {
        return null;
    }
}
function setLocalTabs() {
    const userId = JSON.parse(localStorage.getItem('SYS_PARAMS') || '{}')?.currUserUuid;
    localStorage.setItem(`tabs:${userId}`, JSON.stringify(tabsStore.tabs));
}
const initialState: TabsStoreType = {
    tabs: getLocalTabs() ?? [],
    selectedTab: null,
    excludedTabs: [],
};

window.addEventListener('beforeunload', setLocalTabs);
const beforeSwitchHooks = new Map<string, Array<() => void>>();
const tabsStore = reactive<TabsStoreType>(cloneDeep(initialState));
(window as any).tabs = tabsStore;

const addDynamicTab = (tab: Tab & { isDynamicRoute: true }) => {
    const router: Router = getRouter();
    const cName = guid();
    router.addRoute('SignedBaseLayout', {
        name: `_dynamic-${tab.key}`,
        path: tab.path,
        component: ComponentWrapper(cName),
        props: () => ({ component: tab.component, renderSlots: tab.renderSlots, params: tab.props || {} }),
        meta: { label: tab.label, cName, isDynamicTab: true, tabKey: tab.key },
    });
};

export default {
    setAfterLogin() {
        const maybeRestored = getLocalTabs();
        if (maybeRestored) tabsStore.tabs = maybeRestored;
    },
    setBeforeLogOut() {
        setLocalTabs();
    },
    addTabIfNotExists(tab: Tab, select = false, fromRouter = false) {
        const existing: Tab | undefined = tabsStore.tabs.find((_tab) => _tab.key === tab.key);
        if (tab.cname) {
            const idx = tabsStore.excludedTabs.indexOf(tab.cname);
            if (idx !== -1) tabsStore.excludedTabs.splice(idx, 1);
        }
        if (existing) {
            // existing.fullPath = tab.fullPath;
            if (select) {
                this.setSelectedTab(tab.key);
                if (!fromRouter) getRouter().push(existing.fullPath);
                else {
                    existing.fullPath = tab.fullPath;
                    existing.path = tab.path;
                }
            }
            return;
        }
        if (tab.isDynamicRoute) {
            addDynamicTab(tab);
        }
        tabsStore.tabs.push(tab);
        if (select) {
            if (!tab.isDynamicRoute) this.setSelectedTab(tab.key);
            // setTimeout(() => this.setSelectedTab(tab.key), 100);
            else getRouter().push({ name: `_dynamic-${tab.key}` });
        }
    },
    getTabs() {
        return tabsStore.tabs;
    },
    count() {
        return tabsStore.tabs.length + 1;
    },
    getExcluded() {
        return ['Mainpage', ...tabsStore.excludedTabs];
    },
    getIncluded() {
        return [...tabsStore.tabs.map((t) => t.key)];
    },
    getSelectedTab() {
        return tabsStore.selectedTab;
    },
    getTabIndexByRoutePath(path: string, exact = true) {
        return exact
            ? tabsStore.tabs.findIndex((tab) => tab.fullPath === path)
            : tabsStore.tabs.findIndex((tab) => tab.path === path);
    },
    setSelectedTab(newTab: string | null) {
        if (tabsStore.selectedTab === newTab) return;
        if (tabsStore.selectedTab) beforeSwitchHooks.get(tabsStore.selectedTab)?.forEach((handler) => handler());
        tabsStore.selectedTab = newTab;
    },
    getSelectedTabDef() {
        if (tabsStore.selectedTab) return tabsStore.tabs.find((tab) => tab.key === tabsStore.selectedTab);
    },
    closeTab(router: Router, tabIndex: number) {
        if (tabIndex >= 0) {
            const deletedTab = tabsStore.tabs[tabIndex];
            if (deletedTab.isDynamicRoute && deletedTab.onClose?.()) return;
            const checkRemoveRoute = () => {
                if (deletedTab.isDynamicRoute) {
                    const route = router.getRoutes().find((r) => r.name === `_dynamic-${deletedTab.key}`);
                    if (route && typeof route.meta.cName === 'string') {
                        tabsStore.excludedTabs.push(route.meta.cName);
                    }
                    router.removeRoute(`_dynamic-${deletedTab.key}`);
                } else if (deletedTab.cname) {
                    if (tabsStore.tabs.every((tab) => !tab.cname || tab.cname !== deletedTab.cname)) {
                        const idx = tabsStore.excludedTabs.indexOf(deletedTab.cname);
                        if (idx === -1) tabsStore.excludedTabs.push(deletedTab.cname);
                    }
                }
            };
            beforeSwitchHooks.delete(deletedTab.key);
            tabsStore.tabs.splice(tabIndex, 1);
            if (deletedTab.key === tabsStore.selectedTab) {
                let nextRoute: string | null = null;
                if (tabsStore.tabs.length === 0) {
                    nextRoute = '/';
                } else if (tabIndex < tabsStore.tabs.length) {
                    nextRoute = tabsStore.tabs[tabIndex].fullPath;
                } else {
                    nextRoute = tabsStore.tabs[tabsStore.tabs.length - 1].fullPath;
                }
                (deletedTab.isDynamicRoute ? router.replace(nextRoute) : router.push(nextRoute)).then(checkRemoveRoute);
            }
        }
    },
    closeTabByPath(path: string, router: Router) {
        this.closeTab(router, this.getTabIndexByRoutePath(path));
    },
    reset() {
        Object.assign(tabsStore, cloneDeep(initialState));
    },
    addBeforeSwitchHook(handler: () => void, tabKey = tabsStore.selectedTab): () => void {
        if (!tabKey) return () => {};
        const existingHook = beforeSwitchHooks.get(tabKey);
        if (!existingHook) {
            beforeSwitchHooks.set(tabKey, [handler]);
        } else {
            existingHook.push(handler);
        }
        return () =>
            beforeSwitchHooks.set(tabKey, beforeSwitchHooks.get(tabKey)?.filter((hook) => hook !== handler) || []);
    },
};
